import { Account } from '@lune-climate/lune'
import { BackButton, LuneThemeNew, Text, Tile } from '@lune-fe/lune-ui-lib'
import * as Icons from '@mui/icons-material'
import { Box } from '@mui/material'
import { styled } from '@mui/system'
import { MAIN_NAV_LOCATION_KEY } from 'appConstants'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import LinkTile from 'components/LinkTile'
import TestModeSwitch from 'components/TestModeSwitch'
import useAccounts from 'hooks/useAccounts'
import useActiveOrganisation from 'hooks/useActiveOrganisation'

const SettingsNavigation = () => {
    const { palette } = LuneThemeNew
    const { accounts } = useAccounts()
    const { activeOrg } = useActiveOrganisation()
    const navigate = useNavigate()
    const BackButtonContainer = styled(BackButton)(({ theme }) =>
        theme.unstable_sx({
            pt: 4,
            pl: 3 / 2,
        }),
    )

    const NavigationSection = useMemo(
        () =>
            styled('div')(({ theme }) =>
                theme.unstable_sx({
                    p: 2,
                }),
            ),
        [],
    )

    const StyledHr = styled('hr')(({ theme }) =>
        theme.unstable_sx({
            border: 0,
            height: '1px',
            width: 1,
            background: palette.Grey300,
            margin: 0,
        }),
    )

    return (
        <Box
            data-testId={'settings-navigation-list'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                overflow: 'hidden',
                backgroundColor: palette.Surface100,
            }}
        >
            <Box
                sx={{
                    flex: '1 1 auto',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box>
                    <BackButtonContainer
                        label="Settings"
                        onClick={() => navigate(localStorage.getItem(MAIN_NAV_LOCATION_KEY) || '/')}
                    />
                    <NavigationSection>
                        <LinkTile
                            href={'/settings'}
                            icon={
                                activeOrg?.logo ? (
                                    <Box
                                        sx={{
                                            maxWidth: '24px',
                                            maxHeight: '24px',
                                            img: {
                                                width: '100%',
                                            },
                                        }}
                                    >
                                        <img src={activeOrg.logo} alt={activeOrg.name} />
                                    </Box>
                                ) : (
                                    <Icons.CorporateFare />
                                )
                            }
                        >
                            <Text variant={`body3`}> Organisation </Text>
                        </LinkTile>
                        <LinkTile href={'/settings/billing'} icon={<Icons.ReceiptLongOutlined />}>
                            <Text variant={`body3`}> Billing </Text>
                        </LinkTile>
                    </NavigationSection>
                    <StyledHr />
                </Box>
                <Box sx={{ overflow: 'auto' }}>
                    <NavigationSection>
                        <Tile
                            sx={{
                                '.MuiBox-root': {
                                    position: 'relative',
                                    height: '40px',
                                    borderRadius: '8px !important',
                                    padding: '0 8px 0 12px',
                                },
                                '*': {
                                    color: `${palette.TextPrimary} !important`,
                                },
                                '.MuiListItemIcon-root': {
                                    width: '24px',
                                    height: '24px',
                                    mr: 2,
                                    svg: {
                                        fill: `${palette.TextSecondary} !important`,
                                    },
                                },
                            }}
                            icon={<Icons.AccountBalanceWalletOutlined />}
                        >
                            <Text variant={`body3`}>Accounts</Text>
                        </Tile>
                        <Box sx={{ pl: 5 }} data-testid={`existing-accounts-list`}>
                            {accounts.map((acc: Account) => {
                                return (
                                    <LinkTile
                                        key={`item-${acc.id}`}
                                        href={`/settings/accounts/${acc.id}`}
                                    >
                                        <Text variant={`body3`}> {acc.name} </Text>
                                    </LinkTile>
                                )
                            })}
                        </Box>
                    </NavigationSection>
                </Box>
            </Box>
            <TestModeSwitch />
        </Box>
    )
}

export default SettingsNavigation
